import { useInfiniteQuery } from "@tanstack/react-query"
import { BookOpenCheck, ChevronDown, Clock3 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { getLearningEvents } from "@/lib/api"

import { PageEmpty, PageError, PageLoading } from "./page-state"

export function RecentActivity() {
  const { i18n, t } = useTranslation()
  const query = useInfiniteQuery({
    queryKey: ["learner", "events"],
    queryFn: ({ pageParam }) => getLearningEvents(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  })
  const events = query.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <section
      id="recent-activity"
      className="content-section"
      aria-labelledby="recent-activity-title"
    >
      <header className="content-section-heading">
        <div>
          <p className="eyebrow">{t("home.recentEyebrow")}</p>
          <h2 id="recent-activity-title">{t("home.recentTitle")}</h2>
        </div>
        <Clock3 className="content-section-icon" aria-hidden="true" />
      </header>
      {query.isPending ? <PageLoading label={t("home.recentLoading")} /> : null}
      {query.isError && events.length === 0 ? <PageError retry={() => query.refetch()} /> : null}
      {!query.isPending && !query.isError && events.length === 0 ? (
        <PageEmpty title={t("home.recentEmptyTitle")} description={t("home.recentEmptyBody")} />
      ) : null}
      {events.length ? (
        <Card>
          <CardHeader>
            <BookOpenCheck className="section-icon" aria-hidden="true" />
            <CardTitle>{t("home.recentCardTitle")}</CardTitle>
            <CardDescription>{t("home.recentCardDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="activity-timeline">
              {events.map((event) => (
                <li key={event.id}>
                  <span className="activity-marker" aria-hidden="true" />
                  <div>
                    <strong>
                      {t(`events.${event.eventType}`, { defaultValue: humanize(event.eventType) })}
                    </strong>
                    <time dateTime={event.occurredAt}>
                      {new Intl.DateTimeFormat(i18n.language, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(new Date(event.occurredAt))}
                    </time>
                  </div>
                </li>
              ))}
            </ol>
            {query.isFetchingNextPage ? <p aria-live="polite">{t("home.loadingEarlier")}</p> : null}
            {query.isFetchNextPageError ? <p role="alert">{t("home.earlierError")}</p> : null}
            {query.hasNextPage ? (
              <Button
                className="mt-5"
                variant="secondary"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                <ChevronDown data-icon="inline-start" aria-hidden="true" />
                {t("home.showEarlier")}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </section>
  )
}

function humanize(value: string) {
  return value.replaceAll("_", " ")
}
